/**
 * V10.5: AI-POWERED LEAD SCORING DASHBOARD
 * Real-time lead scoring visualization and management
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Target, TrendingUp, Users, Brain, Award, Activity, Download, RefreshCw } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const LeadScoringDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [topLeads, setTopLeads] = useState([]);
  const [models, setModels] = useState([]);
  const [featureImportance, setFeatureImportance] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // WebSocket connection
  useEffect(() => {
    const socket = new WebSocket('ws://localhost:5000/ws');
    
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.event === 'lead_scored') {
        // Refresh data when new lead is scored
        fetchData();
      }
    };
    
    return () => socket.close();
  }, []);
  
  // Fetch all data
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [analyticsRes, leadsRes, modelsRes, importanceRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/lead-scoring/analytics`),
        axios.get(`${API_BASE_URL}/lead-scoring/top-leads?limit=100`),
        axios.get(`${API_BASE_URL}/lead-scoring/models`),
        axios.get(`${API_BASE_URL}/lead-scoring/feature-importance`)
      ]);
      
      setAnalytics(analyticsRes.data.data);
      setTopLeads(leadsRes.data.data);
      setModels(modelsRes.data.data);
      setFeatureImportance(importanceRes.data.data);
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchScoreHistory = async (leadId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/lead-scoring/lead/${leadId}/score-history`);
      setScoreHistory(response.data.data);
    } catch (error) {
      console.error('Error fetching score history:', error);
    }
  };
  
  const handleScoreLead = async (leadId) => {
    try {
      await axios.post(`${API_BASE_URL}/lead-scoring/score/${leadId}`);
      alert('Lead scored successfully!');
      fetchData();
    } catch (error) {
      console.error('Error scoring lead:', error);
      alert('Failed to score lead');
    }
  };
  
  const handleBatchScore = async () => {
    if (!window.confirm('Start batch scoring for all unscored leads?')) return;
    
    try {
      const response = await axios.post(`${API_BASE_URL}/lead-scoring/batch-score`, {
        limit: 1000
      });
      alert(`Batch job started: ${response.data.data.job_id}`);
      setTimeout(fetchData, 5000); // Refresh after 5 seconds
    } catch (error) {
      console.error('Error starting batch score:', error);
      alert('Failed to start batch scoring');
    }
  };
  
  const handleTrainModel = async () => {
    if (!window.confirm('Train a new lead scoring model? This may take several minutes.')) return;
    
    try {
      const response = await axios.post(`${API_BASE_URL}/lead-scoring/train-model`, {
        model_type: 'random_forest'
      });
      alert(`Model trained successfully! Accuracy: ${(response.data.data.metrics.accuracy * 100).toFixed(2)}%`);
      fetchData();
    } catch (error) {
      console.error('Error training model:', error);
      alert('Failed to train model');
    }
  };
  
  const handleActivateModel = async (modelId) => {
    if (!window.confirm('Activate this model for production use?')) return;
    
    try {
      await axios.post(`${API_BASE_URL}/lead-scoring/models/${modelId}/activate`);
      alert('Model activated successfully!');
      fetchData();
    } catch (error) {
      console.error('Error activating model:', error);
      alert('Failed to activate model');
    }
  };
  
  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981'; // Green - Hot
    if (score >= 60) return '#f59e0b'; // Orange - Warm
    if (score >= 40) return '#3b82f6'; // Blue - Cool
    return '#6b7280'; // Gray - Cold
  };
  
  const getScoreCategory = (score) => {
    if (score >= 80) return 'HOT';
    if (score >= 60) return 'WARM';
    if (score >= 40) return 'COOL';
    return 'COLD';
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading lead scoring data...</p>
        </div>
      </div>
    );
  }
  
  const distributionData = analytics?.score_distribution?.map(item => ({
    name: item.score_category.toUpperCase(),
    value: item.count
  })) || [];
  
  const COLORS = {
    hot: '#10b981',
    warm: '#f59e0b',
    cool: '#3b82f6',
    cold: '#6b7280'
  };
  
  const activityData = analytics?.recent_activity?.map(item => ({
    date: item.date,
    scored: item.leads_scored,
    avgScore: parseFloat(item.avg_score)
  })) || [];
  
  const featureData = featureImportance ? Object.entries(featureImportance)
    .slice(0, 10)
    .map(([feature, importance]) => ({
      feature: feature.replace(/_/g, ' ').toUpperCase(),
      importance: parseFloat((importance * 100).toFixed(2))
    })) : [];
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Brain className="w-10 h-10 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AI Lead Scoring</h1>
              <p className="text-gray-600">Intelligent lead qualification and prediction</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleBatchScore}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
            >
              <Target className="w-4 h-4 mr-2" />
              Batch Score
            </button>
            <button
              onClick={handleTrainModel}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
            >
              <Brain className="w-4 h-4 mr-2" />
              Train Model
            </button>
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-1 bg-white rounded-lg p-1 shadow-sm">
          {['overview', 'top_leads', 'models', 'analytics'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2 rounded-md transition-colors ${
                activeTab === tab
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.replace('_', ' ').toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      
      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Total Scored</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {analytics?.overall_stats?.total_leads_scored || 0}
                  </p>
                </div>
                <Users className="w-12 h-12 text-blue-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Average Score</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {parseFloat(analytics?.overall_stats?.avg_score || 0).toFixed(1)}
                  </p>
                </div>
                <Target className="w-12 h-12 text-green-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Avg Conversion %</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {parseFloat(analytics?.overall_stats?.avg_conversion_prob || 0).toFixed(1)}%
                  </p>
                </div>
                <TrendingUp className="w-12 h-12 text-purple-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Avg Premium</p>
                  <p className="text-3xl font-bold text-gray-900">
                    ${parseFloat(analytics?.overall_stats?.avg_predicted_premium || 0).toFixed(0)}
                  </p>
                </div>
                <Award className="w-12 h-12 text-orange-500" />
              </div>
            </div>
          </div>
          
          {/* Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Score Distribution */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4">Score Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={distributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {distributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[entry.name.toLowerCase()]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Scoring Activity */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4">Recent Scoring Activity</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={activityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="scored" stroke="#3b82f6" name="Leads Scored" />
                  <Line yAxisId="right" type="monotone" dataKey="avgScore" stroke="#10b981" name="Avg Score" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Feature Importance */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4">Top Feature Importance</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={featureData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="feature" type="category" width={150} />
                <Tooltip />
                <Bar dataKey="importance" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {/* Top Leads Tab */}
      {activeTab === 'top_leads' && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Top Scored Leads</h3>
            <p className="text-gray-600">Highest quality leads ranked by AI score</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversion %</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pred. Premium</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pred. LTV</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {topLeads.map((lead, index) => (
                  <tr key={lead.lead_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-gray-900">#{index + 1}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-gray-900">
                          {lead.first_name} {lead.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{lead.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${lead.score}%`,
                              backgroundColor: getScoreColor(lead.score)
                            }}
                          />
                        </div>
                        <span className="font-bold" style={{ color: getScoreColor(lead.score) }}>
                          {parseFloat(lead.score).toFixed(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor: `${getScoreColor(lead.score)}20`,
                          color: getScoreColor(lead.score)
                        }}
                      >
                        {getScoreCategory(lead.score)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-900">
                        {parseFloat(lead.predicted_conversion_probability).toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-900">
                        ${parseFloat(lead.predicted_premium_value).toFixed(0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-900 font-semibold">
                        ${parseFloat(lead.predicted_ltv).toFixed(0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {lead.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          setSelectedLead(lead);
                          fetchScoreHistory(lead.lead_id);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Models Tab */}
      {activeTab === 'models' && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Scoring Models</h3>
            <p className="text-gray-600">Manage and compare ML models</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accuracy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precision</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recall</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">F1 Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ROC-AUC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {models.map(model => (
                  <tr key={model.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{model.model_name}</p>
                        <p className="text-sm text-gray-500">v{model.model_version}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                        {model.model_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold">
                        {(parseFloat(model.accuracy_score) * 100).toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {(parseFloat(model.precision_score) * 100).toFixed(2)}%
                    </td>
                    <td className="px-6 py-4">
                      {(parseFloat(model.recall_score) * 100).toFixed(2)}%
                    </td>
                    <td className="px-6 py-4">
                      {(parseFloat(model.f1_score) * 100).toFixed(2)}%
                    </td>
                    <td className="px-6 py-4">
                      {(parseFloat(model.roc_auc_score) * 100).toFixed(2)}%
                    </td>
                    <td className="px-6 py-4">
                      {model.is_active ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                          ACTIVE
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {!model.is_active && (
                        <button
                          onClick={() => handleActivateModel(model.id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Activate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">
                  {selectedLead.first_name} {selectedLead.last_name}
                </h3>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Score Components */}
              <div>
                <h4 className="font-semibold mb-3">Score Breakdown</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(selectedLead.score_components || {}).map(([key, value]) => (
                    <div key={key} className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-gray-600 text-sm mb-1">{key}</p>
                      <p className="text-2xl font-bold text-gray-900">{parseFloat(value).toFixed(1)}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Score History */}
              <div>
                <h4 className="font-semibold mb-3">Score History</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={scoreHistory}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="scored_at" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadScoringDashboard;
